/**
 * Unified Advertising placement API client.
 * Pages request ads by placement key only — no page-specific forks.
 */
import api from "../../Environment";

function unwrap(response) {
  return response && response.data ? response.data : response;
}

function sessionId() {
  try {
    const key = "tpg_ad_session";
    let id = window.sessionStorage.getItem(key);
    if (!id) {
      id =
        "s_" +
        Math.random().toString(36).slice(2) +
        Date.now().toString(36);
      window.sessionStorage.setItem(key, id);
    }
    return id;
  } catch (e) {
    return null;
  }
}

export function getAdSessionId() {
  return sessionId();
}

export async function fetchAdConfig() {
  try {
    const body = unwrap(await api.postMethod("ads/config", {}));
    if (body && body.success && body.data) {
      return body.data;
    }
  } catch (e) {
    // ignore
  }
  return { enabled: false, placements: {} };
}

/**
 * @param {string|string[]} placementKeyOrKeys
 * @param {object} context
 */
export async function fetchPlacements(placementKeyOrKeys, context = {}) {
  const params = { ...context, session_id: sessionId() };
  if (Array.isArray(placementKeyOrKeys)) {
    // postMethod JSON-encodes arrays; the endpoint expects a comma list.
    params.placement_keys = placementKeyOrKeys.join(",");
  } else {
    params.placement_key = placementKeyOrKeys;
  }
  try {
    const body = unwrap(await api.postMethod("ads/placements", params));
    if (body && body.success && body.data) {
      return body.data.placements || {};
    }
  } catch (e) {
    // ignore
  }
  return {};
}

/*
 * Opt-in batched prefetch: a page that knows its placement keys up front can
 * resolve them in one request (the endpoint resolves each key independently)
 * before its AdPlacements mount. Each prefetched key is consumed once by the
 * next fetchPlacement with the same key + context; stale, mismatched or failed
 * entries fall through to the normal per-key request.
 */
const PREFETCH_TTL_MS = 30000;
const prefetchedPlacements = {};

function prefetchCacheKey(placementKey, context) {
  const ctx = context || {};
  return (
    placementKey +
    "|" +
    Object.keys(ctx)
      .sort()
      .map((k) => k + "=" + JSON.stringify(ctx[k]))
      .join("&")
  );
}

export function prefetchPlacements(placementKeys, context = {}) {
  const keys = (placementKeys || []).filter(Boolean);
  if (!keys.length) return;
  const now = Date.now();
  const pending = keys.every((key) => {
    const entry = prefetchedPlacements[prefetchCacheKey(key, context)];
    return !!entry && entry.expires > now;
  });
  // A quick remount (same keys / context) reuses the unconsumed batch.
  if (pending) return;
  const request = fetchPlacements(keys, context);
  const expires = now + PREFETCH_TTL_MS;
  keys.forEach((key) => {
    prefetchedPlacements[prefetchCacheKey(key, context)] = { request, expires };
  });
}

function takePrefetchedPlacement(placementKey, context) {
  const cacheKey = prefetchCacheKey(placementKey, context);
  const entry = prefetchedPlacements[cacheKey];
  if (!entry) return null;
  delete prefetchedPlacements[cacheKey];
  return entry.expires > Date.now() ? entry.request : null;
}

export async function fetchPlacement(placementKey, context = {}) {
  const prefetched = takePrefetchedPlacement(placementKey, context);
  if (prefetched) {
    const batched = await prefetched;
    if (batched && batched[placementKey]) return batched[placementKey];
  }
  const map = await fetchPlacements(placementKey, context);
  return map[placementKey] || { items: [], fallback_used: false };
}

export async function trackAdImpression(payload) {
  try {
    const body = unwrap(
      await api.postMethod("ads/track/impression", {
        ...payload,
        session_id: sessionId(),
      })
    );
    if (body && body.success && body.data) {
      return body.data;
    }
  } catch (e) {
    // ignore
  }
  return { recorded: false };
}

export async function trackAdClick(payload) {
  try {
    const body = unwrap(
      await api.postMethod("ads/track/click", {
        ...payload,
        session_id: sessionId(),
      })
    );
    if (body && body.success && body.data) {
      return body.data;
    }
  } catch (e) {
    // ignore
  }
  return { recorded: false };
}

export async function fetchAvodPlan(params = {}) {
  try {
    const body = unwrap(await api.postMethod("ads/avod", params));
    if (body && body.success && body.data) {
      return body.data;
    }
  } catch (e) {
    // ignore
  }
  return null;
}

export async function fetchVideoAdSchedule(params = {}) {
  try {
    const body = unwrap(await api.postMethod("ads/schedule", params));
    if (body && body.success && body.data) {
      return body.data;
    }
  } catch (e) {
    // ignore
  }
  return null;
}

export async function trackVideoAdEvent(payload) {
  try {
    const body = unwrap(
      await api.postMethod("ads/track/video", {
        ...payload,
        session_id: sessionId(),
      })
    );
    if (body && body.success && body.data) {
      return body.data;
    }
  } catch (e) {
    // ignore
  }
  return { recorded: false };
}

export async function trackDisplayAdEvent(payload) {
  try {
    const body = unwrap(
      await api.postMethod("ads/track/display", {
        ...payload,
        session_id: payload.session_id || sessionId(),
      })
    );
    if (body && body.success && body.data) {
      return body.data;
    }
  } catch (e) {
    // ignore
  }
  return { recorded: false };
}

export async function fetchPrebidPlan(placementKey, context = {}) {
  try {
    const body = unwrap(
      await api.postMethod("ads/prebid", {
        placement_key: placementKey,
        ...context,
        session_id: sessionId(),
      })
    );
    if (body && body.success && body.data) {
      return body.data;
    }
  } catch (e) {
    // ignore
  }
  return { enabled: false, adUnits: [] };
}

export async function fetchOpenRtbBids(placementKey, context = {}) {
  try {
    const body = unwrap(
      await api.postMethod("ads/openrtb", {
        placement_key: placementKey,
        ...context,
        session_id: sessionId(),
      })
    );
    if (body && body.success && body.data) {
      return body.data;
    }
  } catch (e) {
    // ignore
  }
  return { items: [], reason: "unavailable" };
}

/* —— Admin (platform) —— */

export async function adminAdsMeta() {
  const body = unwrap(await api.postMethod("studio/ads/meta", {}));
  return body;
}

export async function adminAdsCampaigns(params = {}) {
  const body = unwrap(await api.postMethod("studio/ads/campaigns", params));
  return body;
}

export async function adminAdsCampaign(campaignId) {
  const body = unwrap(
    await api.postMethod("studio/ads/campaign", { campaign_id: campaignId })
  );
  return body;
}

export async function adminAdsSave(payload) {
  const body = unwrap(await api.postMethod("studio/ads/campaign/save", payload));
  return body;
}

export async function adminAdsTransition(campaignId, status, extra = {}) {
  const body = unwrap(
    await api.postMethod("studio/ads/campaign/transition", {
      campaign_id: campaignId,
      status,
      ...extra,
    })
  );
  return body;
}

export async function adminAdsPlacements() {
  const body = unwrap(await api.postMethod("studio/ads/placements", {}));
  return body;
}

export async function adminAdsPlacementToggle(key, enabled) {
  const body = unwrap(
    await api.postMethod("studio/ads/placement/toggle", { key, enabled })
  );
  return body;
}

export async function adminAdsAnalytics(params = {}) {
  const body = unwrap(await api.postMethod("studio/ads/analytics", params));
  return body;
}
